import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { COMMS_LIMITS } from "@/lib/comms-limits";
import { appendOlderPage, refreshPagedList, type PagedList } from "@/lib/keyset-list";
import { apiFetch } from "@/services/http/api-fetch";

type Announcement = {
  id: string;
  title: string;
  body: string;
  createdAt: string;
};

type CourseAnnouncementsPanelProps = {
  courseId: string;
  /**
   * Names (and, where there is a choice, changes) the course being notified. When
   * given it replaces the generic "this course" hint, so the target sits beside
   * the form rather than somewhere else on the page.
   */
  courseChooser?: React.ReactNode;
};

export function CourseAnnouncementsPanel({
  courseId,
  courseChooser,
}: CourseAnnouncementsPanelProps) {
  const t = useTranslations("instructor");
  const tRoot = useTranslations();
  const [sent, setSent] = useState<{ count: number; duplicate: boolean } | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // COMMS-12: the newest page is loaded; "Load more" follows the cursor to older ones. The list
  // remembers its course, so a page for a course no longer shown is dropped.
  const [list, setList] = useState<PagedList<Announcement> & { courseId: string }>({
    courseId,
    rows: [],
    cursor: null,
  });
  // R-12: one "Load more" at a time — a second click before the page arrives is ignored.
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);

  const fetchPage = useCallback(
    async (before?: string | null): Promise<PagedList<Announcement> | null> => {
      const query = before ? `?before=${encodeURIComponent(before)}` : "";
      const res = await apiFetch(`/v1/instructor/courses/${courseId}/announcements${query}`);
      if (!res.ok) return null;
      const json = (await res.json()) as {
        data: { announcements: Announcement[]; nextCursor?: string | null };
      };
      return { rows: json.data.announcements, cursor: json.data.nextCursor ?? null };
    },
    [courseId],
  );

  /** After a post: the newest page merged in, keeping older pages already loaded (R-12). */
  const refreshNewest = useCallback(async () => {
    try {
      const page = await fetchPage();
      if (!page) return;
      setList((current) =>
        current.courseId === courseId
          ? { courseId, ...refreshPagedList(current, page, "newestFirst") }
          : current,
      );
    } catch {
      // The list is informational; posting still works without it.
    }
  }, [courseId, fetchPage]);

  async function loadMore() {
    if (!list.cursor || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const page = await fetchPage(list.cursor);
      if (!page) return;
      setList((current) =>
        current.courseId === courseId
          ? { courseId, rows: appendOlderPage(current.rows, page.rows), cursor: page.cursor }
          : current,
      );
    } catch {
      // As above: informational.
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }

  // A course's newest page replaces the list; a late answer for a course no longer shown is dropped.
  useEffect(() => {
    let live = true;
    void (async () => {
      const page = await fetchPage().catch(() => null);
      if (live && page) setList({ courseId, ...page });
    })();
    return () => {
      live = false;
    };
  }, [courseId, fetchPage]);

  const announcements = list.courseId === courseId ? list.rows : [];
  const nextCursor = list.courseId === courseId ? list.cursor : null;

  async function handlePost(event: React.FormEvent) {
    event.preventDefault();
    setPosting(true);
    setError(null);
    setSent(null);
    try {
      const res = await apiFetch(`/v1/instructor/courses/${courseId}/announcements`, {
        method: "POST",
        body: JSON.stringify({ title: title.trim(), body: body.trim() }),
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      const json = (await res.json()) as {
        data: { announcement: { recipientCount?: number; duplicate?: boolean } };
      };
      setSent({
        count: json.data.announcement.recipientCount ?? 0,
        duplicate: Boolean(json.data.announcement.duplicate),
      });
      setTitle("");
      setBody("");
      await refreshNewest();
    } catch (err) {
      setError(
        apiErrorText(err, tRoot, "announcements.failed", { namespace: "announcements.errors" }),
      );
    } finally {
      setPosting(false);
    }
  }

  return (
    <section data-testid="course-announcements-panel">
      <h2 className="text-lg font-medium">{t("announcementsTitle")}</h2>
      {courseChooser ? (
        <div className="mt-3">{courseChooser}</div>
      ) : (
        <p className="mt-1 text-sm text-[var(--text-muted)]">{t("announcementsHint")}</p>
      )}
      <form onSubmit={handlePost} className="mt-4 space-y-3">
        <input
          type="text"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={t("announcementTitlePlaceholder")}
          className="w-full rounded border px-3 py-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
          data-testid="announcement-title"
          maxLength={COMMS_LIMITS.announcementTitle}
          required
        />
        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder={t("announcementBodyPlaceholder")}
          rows={3}
          className="w-full rounded border px-3 py-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
          data-testid="announcement-body"
          maxLength={COMMS_LIMITS.announcementBody}
          required
        />
        {error ? (
          <p role="alert" className="text-sm text-red-600" data-testid="announcement-error">
            {error}
          </p>
        ) : null}
        {sent ? (
          <p
            role="status"
            className="text-sm text-[var(--text-secondary)]"
            data-testid="announcement-sent"
          >
            {sent.duplicate
              ? tRoot("announcements.alreadySent")
              : tRoot("announcements.sentTo", { count: sent.count })}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={posting}
          className="rounded bg-[var(--ink-9)] px-4 py-2 text-sm text-white disabled:opacity-50"
          data-testid="announcement-post"
        >
          {posting ? t("announcementPosting") : t("announcementPost")}
        </button>
      </form>
      <ul className="mt-6 space-y-3">
        {announcements.map((announcement) => (
          <li
            key={announcement.id}
            className="rounded border border-[var(--border)] p-3 text-sm"
            data-testid={`announcement-item-${announcement.id}`}
          >
            <p className="font-medium">{announcement.title}</p>
            <p className="mt-1 whitespace-pre-wrap text-[var(--text-secondary)]">
              {announcement.body}
            </p>
          </li>
        ))}
      </ul>
      {nextCursor ? (
        <button
          type="button"
          className="mt-3 text-sm underline disabled:opacity-50"
          disabled={loadingMore}
          onClick={() => void loadMore()}
          data-testid="announcements-more"
        >
          {tRoot("announcements.loadMore")}
        </button>
      ) : null}
    </section>
  );
}
